import React, { useState, useContext, createContext } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { 
  ArrowRight, 
  BookOpen, 
  Edit, 
  Eye, 
  Users, 
  Settings, 
  ChevronDown,
  Home,
  Search,
  Plus,
  User,
  Shield,
  BarChart3,
  Lightbulb,
  Target,
  Compass,
  Globe,
  Layers
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';

// Unified Navigation & Terminology for The Academy Platform
// Ensures consistent terminology and seamless transitions between modes

// Context for managing navigation state and mode transitions
interface NavigationContextType {
  currentMode: 'learning' | 'contributing' | 'admin';
  userRole: 'student' | 'contributor' | 'admin' | 'super_admin';
  canTransition: (toMode: 'learning' | 'contributing' | 'admin') => boolean;
  transitionTo: (mode: 'learning' | 'contributing' | 'admin') => void;
  terminology: TerminologySet;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

// Unified terminology across the platform
interface TerminologySet {
  content: {
    singular: string;
    plural: string;
    create: string;
    edit: string;
    view: string;
  };
  user: {
    singular: string;
    plural: string;
    profile: string;
  };
  progress: {
    learning: string;
    completion: string;
    mastery: string;
  };
  navigation: {
    home: string;
    explore: string;
    create: string;
    manage: string;
    profile: string;
    admin: string;
  };
  actions: {
    start: string;
    continue: string;
    complete: string;
    submit: string;
    save: string;
    publish: string;
  };
}

const getTerminologyForMode = (mode: 'learning' | 'contributing' | 'admin'): TerminologySet => {
  const base: TerminologySet = {
    content: {
      singular: 'Node',
      plural: 'Nodes',
      create: 'Create Node',
      edit: 'Edit Node',
      view: 'View Node'
    },
    user: {
      singular: 'Scholar',
      plural: 'Scholars',
      profile: 'Scholar Profile'
    },
    progress: {
      learning: 'Learning Journey',
      completion: 'Mastery',
      mastery: 'Expertise Level'
    },
    navigation: {
      home: 'Academy',
      explore: 'Explore',
      create: 'Contribute',
      manage: 'Manage',
      profile: 'Profile',
      admin: 'Administration'
    },
    actions: {
      start: 'Begin Learning',
      continue: 'Continue',
      complete: 'Mark Complete',
      submit: 'Submit',
      save: 'Save Progress',
      publish: 'Publish'
    }
  };

  switch (mode) {
    case 'learning':
      return {
        ...base,
        content: {
          ...base.content,
          view: 'Study Node',
          create: 'Suggest Content'
        },
        actions: {
          ...base.actions,
          start: 'Begin Study',
          continue: 'Continue Learning',
          save: 'Bookmark'
        }
      };
    
    case 'contributing':
      return {
        ...base,
        content: {
          ...base.content,
          create: 'Create Knowledge Node',
          edit: 'Refine Node'
        },
        user: {
          ...base.user,
          singular: 'Contributor',
          plural: 'Contributors'
        },
        actions: {
          ...base.actions,
          start: 'Begin Contributing',
          save: 'Save Draft'
        }
      };
    
    case 'admin':
      return {
        ...base,
        content: {
          ...base.content,
          singular: 'Content Item',
          plural: 'Content Items',
          view: 'Review Content'
        },
        user: {
          ...base.user,
          singular: 'User',
          plural: 'Users'
        },
        navigation: {
          ...base.navigation,
          home: 'Dashboard'
        },
        actions: {
          ...base.actions,
          start: 'Manage',
          save: 'Update'
        }
      };
    
    default:
      return base;
  }
};

// Main navigation items configuration
interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  href: string;
  description?: string;
  modes: Array<'learning' | 'contributing' | 'admin'>;
  roles: Array<'student' | 'contributor' | 'admin' | 'super_admin'>;
  badge?: string;
  children?: NavigationItem[];
}

const getNavigationItems = (mode: 'learning' | 'contributing' | 'admin', terminology: TerminologySet): NavigationItem[] => {
  const baseItems: NavigationItem[] = [
    {
      id: 'home',
      label: terminology.navigation.home,
      icon: Home,
      href: mode === 'admin' ? '/admin' : '/',
      description: 'Main hub for your academic journey',
      modes: ['learning', 'contributing', 'admin'],
      roles: ['student', 'contributor', 'admin', 'super_admin']
    },
    {
      id: 'explore',
      label: terminology.navigation.explore,
      icon: Compass,
      href: '/search',
      description: 'Discover knowledge across disciplines',
      modes: ['learning', 'contributing'],
      roles: ['student', 'contributor', 'admin', 'super_admin']
    },
    {
      id: 'convergence',
      label: 'Convergence',
      icon: Globe,
      href: '/convergence',
      description: 'Interdisciplinary connections',
      modes: ['learning', 'contributing'],
      roles: ['student', 'contributor', 'admin', 'super_admin']
    }
  ];

  // Mode-specific items
  if (mode === 'learning') {
    baseItems.push(
      {
        id: 'my-learning',
        label: 'My Learning',
        icon: BookOpen,
        href: '/dashboard',
        description: 'Your personalized learning dashboard',
        modes: ['learning'],
        roles: ['student', 'contributor', 'admin', 'super_admin']
      },
      {
        id: 'progress',
        label: 'Progress',
        icon: Target,
        href: '/progress',
        description: 'Track your learning journey',
        modes: ['learning'],
        roles: ['student', 'contributor', 'admin', 'super_admin']
      }
    );
  }

  if (mode === 'contributing') {
    baseItems.push(
      {
        id: 'create',
        label: terminology.content.create,
        icon: Plus,
        href: '/create',
        description: 'Contribute new knowledge',
        modes: ['contributing'],
        roles: ['contributor', 'admin', 'super_admin']
      },
      {
        id: 'my-contributions',
        label: 'My Contributions',
        icon: Edit,
        href: '/contributions',
        description: 'Manage your content',
        modes: ['contributing'],
        roles: ['contributor', 'admin', 'super_admin']
      }
    );
  }

  if (mode === 'admin') {
    baseItems.push(
      {
        id: 'users',
        label: 'User Management',
        icon: Users,
        href: '/admin/users',
        description: 'Manage platform users',
        modes: ['admin'],
        roles: ['admin', 'super_admin']
      },
      {
        id: 'content',
        label: 'Content Management',
        icon: Layers,
        href: '/admin/content',
        description: 'Oversee all platform content',
        modes: ['admin'],
        roles: ['admin', 'super_admin']
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        href: '/admin/analytics',
        description: 'Platform insights and metrics',
        modes: ['admin'],
        roles: ['admin', 'super_admin']
      }
    );
  }

  return baseItems;
};

// Navigation Provider Component
interface NavigationProviderProps {
  children: React.ReactNode;
  initialMode?: 'learning' | 'contributing' | 'admin';
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ 
  children, 
  initialMode = 'learning' 
}) => {
  const { user } = useAuth();
  const [currentMode, setCurrentMode] = useState<'learning' | 'contributing' | 'admin'>(initialMode);
  
  const userRole = user?.user_metadata?.role || 'student';
  const terminology = getTerminologyForMode(currentMode);

  const canTransition = (toMode: 'learning' | 'contributing' | 'admin'): boolean => {
    if (toMode === 'learning') return true; // Anyone can learn
    if (toMode === 'contributing') return ['contributor', 'admin', 'super_admin'].includes(userRole);
    if (toMode === 'admin') return ['admin', 'super_admin'].includes(userRole);
    return false;
  };

  const transitionTo = (mode: 'learning' | 'contributing' | 'admin') => {
    if (canTransition(mode)) {
      setCurrentMode(mode);
    }
  };

  const contextValue: NavigationContextType = {
    currentMode,
    userRole,
    canTransition,
    transitionTo,
    terminology
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Mode Transition Component
export interface ModeTransitionProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'pills' | 'tabs' | 'buttons';
}

export const ModeTransition: React.FC<ModeTransitionProps> = ({
  className,
  showLabels = true,
  variant = 'pills'
}) => {
  const { currentMode, canTransition, transitionTo, terminology } = useNavigation();

  const modes = [
    {
      id: 'learning' as const,
      label: 'Learning',
      description: 'Explore and study knowledge',
      icon: BookOpen,
      color: 'bg-white text-black border-blue-200'
    },
    {
      id: 'contributing' as const,
      label: 'Contributing',
      description: 'Create and share knowledge',
      icon: Edit,
      color: 'bg-white text-green-700 border-green-200'
    },
    {
      id: 'admin' as const,
      label: 'Administration',
      description: 'Manage platform',
      icon: Shield,
      color: 'bg-white text-black border-purple-200'
    }
  ].filter(mode => canTransition(mode.id));

  if (modes.length <= 1) return null; // Don't show if user can only access one mode

  const renderModeButton = (mode: typeof modes[0]) => {
    const Icon = mode.icon;
    const isActive = currentMode === mode.id;

    if (variant === 'pills') {
      return (
        <Button
          key={mode.id}
          variant={isActive ? 'default' : 'outline'}
          size="sm"
          onClick={() => transitionTo(mode.id)}
          className={cn(
            'flex items-center gap-2 transition-all',
            isActive && mode.color
          )}
        >
          <Icon className="h-4 w-4" />
          {showLabels && mode.label}
        </Button>
      );
    }

    if (variant === 'tabs') {
      return (
        <button
          key={mode.id}
          onClick={() => transitionTo(mode.id)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors',
            isActive
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-black hover:text-foreground'
          )}
        >
          <Icon className="h-4 w-4" />
          {showLabels && mode.label}
        </button>
      );
    }

    return (
      <Card
        key={mode.id}
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          isActive && 'ring-2 ring-primary'
        )}
        onClick={() => transitionTo(mode.id)}
      >
        <CardContent className="p-4 text-center">
          <Icon className={cn('h-8 w-8 mx-auto mb-2', isActive ? 'text-primary' : 'text-black')} />
          {showLabels && (
            <>
              <h3 className="font-medium">{mode.label}</h3>
              <p className="text-sm text-black mt-1">{mode.description}</p>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={cn(
      'flex items-center gap-2',
      variant === 'buttons' && 'grid grid-cols-3 gap-4',
      variant === 'tabs' && 'border-b',
      className
    )}>
      {modes.map(renderModeButton)}
    </div>
  );
};

// Unified Navigation Menu Component
export interface UnifiedNavigationMenuProps {
  orientation?: 'horizontal' | 'vertical';
  showIcons?: boolean;
  showDescriptions?: boolean;
  className?: string;
}

export const UnifiedNavigationMenu: React.FC<UnifiedNavigationMenuProps> = ({
  orientation = 'horizontal',
  showIcons = true,
  showDescriptions = false,
  className
}) => {
  const { currentMode, userRole, terminology } = useNavigation();
  const navigationItems = getNavigationItems(currentMode, terminology);

  const filteredItems = navigationItems.filter(item => 
    item.modes.includes(currentMode) && item.roles.includes(userRole)
  );

  const renderItem = (item: NavigationItem) => {
    const Icon = item.icon;
    
    return (
      <Link
        key={item.id}
        to={item.href}
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors',
          orientation === 'vertical' && 'w-full',
          orientation === 'horizontal' && 'whitespace-nowrap'
        )}
      >
        {showIcons && <Icon className="h-5 w-5 text-black" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{item.label}</span>
            {item.badge && (
              <Badge variant="secondary" className="text-xs">
                {item.badge}
              </Badge>
            )}
          </div>
          {showDescriptions && item.description && (
            <p className="text-sm text-black">{item.description}</p>
          )}
        </div>
        {orientation === 'horizontal' && (
          <ArrowRight className="h-4 w-4 text-black opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </Link>
    );
  };

  return (
    <nav className={cn(
      'space-y-2',
      orientation === 'horizontal' && 'flex items-center space-y-0 space-x-4',
      className
    )}>
      {filteredItems.map(renderItem)}
    </nav>
  );
};

// Terminology Display Component
export interface TerminologyDisplayProps {
  category: keyof TerminologySet;
  term: string;
  className?: string;
}

export const TerminologyDisplay: React.FC<TerminologyDisplayProps> = ({
  category,
  term,
  className
}) => {
  const { terminology } = useNavigation();
  const value = terminology[category][term as keyof TerminologySet[keyof TerminologySet]];
  
  return <span className={className}>{value}</span>;
};

// Quick transition buttons for common flows
export const QuickTransitions: React.FC<{ className?: string }> = ({ className }) => {
  const { currentMode, canTransition, transitionTo } = useNavigation();

  const transitions = [
    {
      from: 'learning',
      to: 'contributing' as const,
      label: 'Start Contributing',
      description: 'Share your knowledge',
      icon: Plus
    },
    {
      from: 'contributing',
      to: 'learning' as const,
      label: 'Continue Learning',
      description: 'Explore more topics',
      icon: BookOpen
    },
    {
      from: 'admin',
      to: 'learning' as const,
      label: 'Student View',
      description: 'See learner experience',
      icon: Eye
    }
  ];

  const availableTransitions = transitions.filter(t => 
    t.from === currentMode && canTransition(t.to)
  );

  if (availableTransitions.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {availableTransitions.map((transition) => {
        const Icon = transition.icon;
        return (
          <Button
            key={`${transition.from}-${transition.to}`}
            variant="outline"
            size="sm"
            onClick={() => transitionTo(transition.to)}
            className="flex items-center gap-2"
          >
            <Icon className="h-4 w-4" />
            {transition.label}
          </Button>
        );
      })}
    </div>
  );
};
